import React, { useEffect, useState, useMemo } from 'react';
import { Drawer, Divider, Avatar, Dropdown, Menu } from 'antd';
import { connect } from 'react-redux';
import { onSideNavToggle, onLocaleChange } from 'redux/actions/Theme';
import { signOut } from 'redux/actions/Auth';
import { onUserInfoChange } from 'redux/actions/Waan';
import Flex from 'components/shared-components/Flex';
import {
  CloseOutlined,
  LogoutOutlined,
  EditOutlined,
  DownOutlined,
  UpOutlined,
  CheckCircleFilled,
  QrcodeOutlined,
  BarChartOutlined,
  FileTextOutlined,
  RightOutlined,
  ExportOutlined,
  ShopOutlined,
  UsergroupAddOutlined,
  ClusterOutlined,
  AppstoreAddOutlined,
  CreditCardOutlined,
  UserOutlined,
  SafetyOutlined,
  BellOutlined,
  SolutionOutlined,
  HistoryOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { Scrollbars } from 'react-custom-scrollbars-2';
import {
  getTranslate,
  checkNull,
  cleanCookieAuth,
  GetDfaultCompanieImg,
} from 'components/CustomService';
import { getTextRights } from 'views/app-views/settings/staffList/staff-list';
import { APP_PREFIX_PATH } from 'configs/AppConfig';
import { useLocation, Link } from 'react-router-dom';
import 'assets/css/layout/MobileSlideBarNav.css';

const isProduction = process.env.REACT_APP_ENV === 'production';

const NFCpathsToCheck = [
  '/apps/nfc/index',
  '/apps/nfc/detail',
  '/apps/nfc/customize-qr',
  '/apps/page-builder',
];

const accountPaths = [
  '/profile/edit-profile',
  '/profile/movement',
  '/profile/safety',
  '/profile/company',
  '/profile/package',
  '/profile/billing',
  '/profile/notification',
  '/profile/manage-data-usage',
  '/profile/api-developers',
];

export const MobileSlideBarNav = ({
  slideBarNav,
  onSideNavToggle,
  signOut,
  onUserInfoChange,
  onLocaleChange,
  locale,
  waan,
}) => {
  let location = useLocation();
  const userInfo = waan?.userInfo || {};
  const companies = waan?.companies || [];
  const { isAdmin = false } = userInfo;
  const admin_rights = userInfo?.admin_rights;

  const isPermissionApi = useMemo(() => {
    return ![0].includes(admin_rights) && !isAdmin ? false : true;
  }, [admin_rights, isAdmin]);

  const [ImgAvatar, setImgAvatar] = useState(null);
  // Auto expand state for Profile & System submenu (only expand when active, collapse when not active)
  const isAccountActive = accountPaths.some((p) => location.pathname.includes(p));
  const [expandedAccount, setExpandedAccount] = useState(isAccountActive);

  useEffect(() => {
    setExpandedAccount(isAccountActive);
  }, [location.pathname, slideBarNav, isAccountActive]);

  useEffect(() => {
    if (checkNull(userInfo.img_avatar) && checkNull(userInfo.path_img_avatar)) {
      setImgAvatar(
        `${process.env.REACT_APP_PathCDN}/${userInfo.path_img_avatar}/${userInfo.img_avatar}`,
      );
    }
  }, [userInfo]);

  const onClose = () => {
    onSideNavToggle(false);
  };

  const currentCompany = (companies || []).find((c) => c.id === Number(userInfo.companie_id));

  const handleSelectCompany = (m) => {
    const admin_rights = m.attributes?.staff_admin?.attributes?.admin_rights;
    onUserInfoChange({
      ...userInfo,
      companie_id: m.id,
      companie_name: m.attributes?.name,
      companie_uid: m.attributes?.uid,
      admin_rights: admin_rights == null ? null : admin_rights,
    });
    onClose();
  };

  const companyMenu = (
    <Menu style={{ maxHeight: '280px', overflowY: 'auto', minWidth: '240px' }}>
      {(companies || []).map((m) => {
        const isEnable =
          m.attributes?.staff_admin?.attributes?.enable === true &&
          m.attributes?.staff_admin?.attributes?.status === true;
        const isSelected = Number(userInfo.companie_id) === m.id;

        return (
          <Menu.Item
            disabled={!isEnable}
            key={m.id}
            onClick={() => handleSelectCompany(m)}
            className="py-2">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center text-truncate mr-2" style={{ minWidth: 0 }}>
                <Avatar size={32} src={GetDfaultCompanieImg(m)} className="mr-2 flex-shrink-0" />
                <div className="text-truncate" style={{ maxWidth: '180px', lineHeight: '1.4' }}>
                  <div
                    className="font-weight-medium text-dark text-truncate"
                    style={{ fontSize: '13.5px' }}>
                    {m.attributes?.name}
                  </div>
                  <div className="text-muted text-truncate" style={{ fontSize: '12px' }}>
                    {getTextRights(m?.attributes?.staff_admin)}
                  </div>
                </div>
              </div>
              {isSelected ? (
                <CheckCircleFilled className="text-primary flex-shrink-0 ml-2" />
              ) : null}
            </div>
          </Menu.Item>
        );
      })}
      <Menu.Divider className="my-1" />
      <Menu.Item key="view-all-companies" onClick={onClose} className="text-center py-2">
        <Link
          to="/app/profile/company"
          className="text-primary font-weight-medium"
          style={{ fontSize: '13px' }}>
          {getTranslate('view_all')}
        </Link>
      </Menu.Item>
    </Menu>
  );

  const handleLanguageChange = (langId) => {
    localStorage.setItem('language', langId);
    onLocaleChange(langId);
  };

  const handleLogout = () => {
    onClose();
    signOut();
    cleanCookieAuth();
  };

  const renderSubItem = (to, icon, title, isActive) => (
    <Link
      to={to}
      onClick={onClose}
      key={to}
      className={`mobile-slidebar-sub-item ${isActive ? 'is-active' : ''}`}>
      <div className="d-flex align-items-center text-truncate mr-2" style={{ minWidth: 0 }}>
        <span className={`mobile-slidebar-sub-icon ${isActive ? 'is-active' : ''}`}>{icon}</span>
        <span className={`mobile-slidebar-sub-text text-truncate ${isActive ? 'is-active' : ''}`}>
          {title}
        </span>
      </div>
      <RightOutlined className={`mobile-slidebar-sub-arrow ${isActive ? 'is-active' : ''}`} />
    </Link>
  );

  return (
    <Drawer
      className="ant-drawer-body-side-bar mobile-slidebar-nav"
      placement="left"
      closable={false}
      width={typeof window !== 'undefined' ? Math.min(window.innerWidth * 0.85, 320) : 300}
      onClose={onClose}
      visible={slideBarNav}
      bodyStyle={{ padding: 0 }}>
      <Flex flexDirection="column" className="h-100" style={{ height: '100%' }}>
        {/* Mobile Header Bar - User Profile */}
        <div className="mobile-slidebar-header">
          <Flex alignItems="center" className="flex-grow-1 overflow-hidden mr-2">
            <Avatar size={40} src={ImgAvatar} className="flex-shrink-0" />
            <div className="pl-3 flex-grow-1 text-truncate">
              <div className="mobile-slidebar-user-name text-truncate">
                {userInfo?.firstname} {userInfo?.lastname}
              </div>
              <Link
                to="/app/profile"
                onClick={onClose}
                className="mobile-slidebar-user-edit text-muted d-inline-flex align-items-center">
                <EditOutlined className="mr-1" style={{ fontSize: '11px' }} />
                <span>{getTranslate('editProfile')}</span>
              </Link>
            </div>
          </Flex>
          <div className="mobile-slidebar-nav-close" onClick={onClose}>
            <CloseOutlined />
          </div>
        </div>

        {/* Upper Menu Body (Original Order) */}
        <div className="mobile-slidebar-body">
          <Scrollbars autoHide>
            <div className="mobile-slidebar-content">
              {/* GROUP 1: Waan Tid / NFC & QR */}
              <div className="mobile-slidebar-group-title">
                {getTranslate('Waan_Tid')} / NFC & QR
              </div>

              <div className="mobile-slidebar-nav-list mb-3">
                {/* 1. NFC & QR Link */}
                {(() => {
                  const isNfcActive = NFCpathsToCheck.some((path) =>
                    location.pathname.includes(path),
                  );
                  return (
                    <Link
                      to={`${APP_PREFIX_PATH}/apps/nfc/index`}
                      onClick={onClose}
                      className={`mobile-slidebar-nav-link ${isNfcActive ? 'is-active' : ''}`}>
                      <div
                        className="d-flex align-items-center text-truncate mr-2"
                        style={{ minWidth: 0 }}>
                        <div
                          className={`mobile-slidebar-icon-box ${isNfcActive ? 'is-active' : ''}`}>
                          <QrcodeOutlined />
                        </div>
                        <span className="mobile-slidebar-nav-text text-truncate">
                          {getTranslate('NFC_QR_Link')}
                        </span>
                      </div>
                    </Link>
                  );
                })()}

                {/* 2. Statistics Report */}
                {(() => {
                  const isStatActive = location.pathname.includes('/apps/nfc/statistics');
                  return (
                    <Link
                      to={`${APP_PREFIX_PATH}/apps/nfc/statistics`}
                      onClick={onClose}
                      className={`mobile-slidebar-nav-link ${isStatActive ? 'is-active' : ''}`}>
                      <div
                        className="d-flex align-items-center text-truncate mr-2"
                        style={{ minWidth: 0 }}>
                        <div
                          className={`mobile-slidebar-icon-box ${isStatActive ? 'is-active' : ''}`}>
                          <BarChartOutlined />
                        </div>
                        <span className="mobile-slidebar-nav-text text-truncate">
                          {getTranslate('statistics_report')}
                        </span>
                      </div>
                    </Link>
                  );
                })()}

                {/* 3. Instruction Manual (External Link) */}
                <a
                  href="https://www.waankit.com/blogs/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className="mobile-slidebar-nav-link">
                  <div
                    className="d-flex align-items-center text-truncate mr-2"
                    style={{ minWidth: 0 }}>
                    <div className="mobile-slidebar-icon-box">
                      <FileTextOutlined />
                    </div>
                    <span className="mobile-slidebar-nav-text text-truncate">
                      {getTranslate('Instruction_manual')}
                    </span>
                  </div>
                  <ExportOutlined className="mobile-slidebar-external-icon text-muted" />
                </a>
              </div>

              <Divider className="my-2" />

              {/* GROUP 2: Settings (ตั้งค่า) */}
              <div className="mobile-slidebar-group-title">
                {getTranslate('setting1') || 'ตั้งค่า'}
              </div>

              <div className="mobile-slidebar-nav-list mb-3">
                {/* 1. Collapsible Submenu: โปรไฟล์ (Profile) */}
                <div>
                  <div
                    className={`mobile-slidebar-nav-link cursor-pointer ${
                      isAccountActive ? 'is-active' : ''
                    }`}
                    onClick={() => setExpandedAccount(!expandedAccount)}>
                    <div
                      className="d-flex align-items-center text-truncate mr-2"
                      style={{ minWidth: 0 }}>
                      <div
                        className={`mobile-slidebar-icon-box ${
                          isAccountActive ? 'is-active' : ''
                        }`}>
                        <UserOutlined />
                      </div>
                      <span className="mobile-slidebar-nav-text text-truncate">
                        {getTranslate('profile') || 'โปรไฟล์'}
                      </span>
                    </div>
                    {expandedAccount ? (
                      <UpOutlined className="mobile-slidebar-arrow text-muted" />
                    ) : (
                      <DownOutlined className="mobile-slidebar-arrow text-muted" />
                    )}
                  </div>

                  {/* Submenu Items: All 9 Profile Items */}
                  {expandedAccount && (
                    <div className="mobile-slidebar-submenu-wrap">
                      {renderSubItem(
                        `${APP_PREFIX_PATH}/profile/edit-profile`,
                        <UserOutlined />,
                        getTranslate('settingProfile') || getTranslate('editProfile'),
                        location.pathname.includes('/profile/edit-profile'),
                      )}
                      {renderSubItem(
                        `${APP_PREFIX_PATH}/profile/movement`,
                        <HistoryOutlined />,
                        getTranslate('movement'),
                        location.pathname.includes('/profile/movement'),
                      )}
                      {renderSubItem(
                        `${APP_PREFIX_PATH}/profile/safety`,
                        <SafetyOutlined />,
                        getTranslate('safety'),
                        location.pathname.includes('/profile/safety'),
                      )}
                      {renderSubItem(
                        `${APP_PREFIX_PATH}/profile/company`,
                        <ClusterOutlined />,
                        getTranslate('company'),
                        location.pathname.includes('/profile/company'),
                      )}
                      {renderSubItem(
                        `${APP_PREFIX_PATH}/profile/package`,
                        <AppstoreAddOutlined />,
                        getTranslate('Packages_Quota1') ||
                          `${getTranslate('package')}/${getTranslate('quota')}`,
                        location.pathname.includes('/profile/package'),
                      )}
                      {renderSubItem(
                        `${APP_PREFIX_PATH}/profile/billing`,
                        <CreditCardOutlined />,
                        getTranslate('billing'),
                        location.pathname.includes('/profile/billing'),
                      )}
                      {renderSubItem(
                        `${APP_PREFIX_PATH}/profile/notification`,
                        <BellOutlined />,
                        getTranslate('notification'),
                        location.pathname.includes('/profile/notification'),
                      )}
                      {renderSubItem(
                        `${APP_PREFIX_PATH}/profile/manage-data-usage`,
                        <SolutionOutlined />,
                        getTranslate('manage_data_usage'),
                        location.pathname.includes('/profile/manage-data-usage'),
                      )}
                      {!isProduction &&
                        isPermissionApi &&
                        renderSubItem(
                          `${APP_PREFIX_PATH}/profile/api-developers`,
                          <ApiOutlined />,
                          getTranslate('API_for_developers'),
                          location.pathname.includes('/profile/api-developers'),
                        )}
                    </div>
                  )}
                </div>

                {/* 2. จัดการสาขา (Branch Management) */}
                {(() => {
                  const isBranchActive = location.pathname.includes('/setting-shop/branch');
                  return (
                    <Link
                      to={`${APP_PREFIX_PATH}/setting-shop/branch/branch-list`}
                      onClick={onClose}
                      className={`mobile-slidebar-nav-link ${isBranchActive ? 'is-active' : ''}`}>
                      <div
                        className="d-flex align-items-center text-truncate mr-2"
                        style={{ minWidth: 0 }}>
                        <div
                          className={`mobile-slidebar-icon-box ${
                            isBranchActive ? 'is-active' : ''
                          }`}>
                          <ShopOutlined />
                        </div>
                        <span className="mobile-slidebar-nav-text text-truncate">
                          {getTranslate('branch_management')}
                        </span>
                      </div>
                    </Link>
                  );
                })()}

                {/* 3. ผู้ดูแลระบบ (Admin / Staff Management) */}
                {(() => {
                  const isStaffActive = location.pathname.includes('/setting-shop/staff');
                  return (
                    <Link
                      to={`${APP_PREFIX_PATH}/setting-shop/staff/staff-list`}
                      onClick={onClose}
                      className={`mobile-slidebar-nav-link ${isStaffActive ? 'is-active' : ''}`}>
                      <div
                        className="d-flex align-items-center text-truncate mr-2"
                        style={{ minWidth: 0 }}>
                        <div
                          className={`mobile-slidebar-icon-box ${
                            isStaffActive ? 'is-active' : ''
                          }`}>
                          <UsergroupAddOutlined />
                        </div>
                        <span className="mobile-slidebar-nav-text text-truncate">
                          {getTranslate('sidenav.groups3.reports.staff') || 'ผู้ดูแลระบบ'}
                        </span>
                      </div>
                    </Link>
                  );
                })()}

                {/* 4. ประวัติการทำงานของระบบ (System Activity) */}
                {(() => {
                  const isSystemActive = location.pathname.includes('/apps/system/system-activity');
                  return (
                    <Link
                      to={`${APP_PREFIX_PATH}/apps/system/system-activity`}
                      onClick={onClose}
                      className={`mobile-slidebar-nav-link ${isSystemActive ? 'is-active' : ''}`}>
                      <div
                        className="d-flex align-items-center text-truncate mr-2"
                        style={{ minWidth: 0 }}>
                        <div
                          className={`mobile-slidebar-icon-box ${
                            isSystemActive ? 'is-active' : ''
                          }`}>
                          <HistoryOutlined />
                        </div>
                        <span className="mobile-slidebar-nav-text text-truncate">
                          {getTranslate('movement_system') || 'ประวัติการทำงานของระบบ'}
                        </span>
                      </div>
                    </Link>
                  );
                })()}
              </div>
            </div>
          </Scrollbars>
        </div>

        {/* Bottom Area (Original Order) */}
        <div className="mobile-slidebar-footer">
          {/* Organization Switcher Card */}
          {!isAdmin && currentCompany && (
            <div className="mb-2">
              <Dropdown overlay={companyMenu} trigger={['click']} placement="bottomLeft">
                <div className="mobile-slidebar-workspace-card">
                  <div
                    className="d-flex align-items-center text-truncate mr-2 flex-grow-1"
                    style={{ minWidth: 0 }}>
                    <Avatar
                      size={34}
                      src={GetDfaultCompanieImg(currentCompany)}
                      className="mr-2 flex-shrink-0"
                    />
                    <div className="text-truncate flex-grow-1" style={{ lineHeight: '1.4' }}>
                      <div className="mobile-slidebar-company-name text-truncate">
                        {currentCompany.attributes?.name}
                      </div>
                      <div className="mobile-slidebar-company-role text-muted text-truncate">
                        {getTextRights(currentCompany?.attributes?.staff_admin)}
                      </div>
                    </div>
                  </div>
                  <DownOutlined className="mobile-slidebar-workspace-arrow text-muted flex-shrink-0" />
                </div>
              </Dropdown>
            </div>
          )}

          <Divider className="my-2" />

          {/* Language Switcher (Segmented Button) */}
          <div className="mb-2">
            <div className="mobile-slidebar-lang-wrap">
              <div
                className={`mobile-slidebar-lang-btn ${locale === 'th' ? 'is-active' : ''}`}
                onClick={() => handleLanguageChange('th')}>
                <img src="/img/flags/th.png" alt="TH" className="mobile-slidebar-flag-img" />
                <span>ภาษาไทย</span>
              </div>
              <div
                className={`mobile-slidebar-lang-btn ${locale === 'en' ? 'is-active' : ''}`}
                onClick={() => handleLanguageChange('en')}>
                <img src="/img/flags/us.png" alt="EN" className="mobile-slidebar-flag-img" />
                <span>English</span>
              </div>
            </div>
          </div>

          {/* Logout Action Row */}
          <div className="mobile-slidebar-logout-btn" onClick={handleLogout}>
            <LogoutOutlined className="mr-2 text-danger" />
            <span>{getTranslate('logOut')}</span>
          </div>
        </div>
      </Flex>
    </Drawer>
  );
};

MobileSlideBarNav.propTypes = {
  slideBarNav: PropTypes.bool,
  onSideNavToggle: PropTypes.func,
  signOut: PropTypes.func,
  onUserInfoChange: PropTypes.func,
  onLocaleChange: PropTypes.func,
  locale: PropTypes.string,
  waan: PropTypes.object,
};

const mapStateToProps = ({ theme, waan }) => {
  const { slideBarNav, locale } = theme;
  return { slideBarNav, locale, waan };
};

export default connect(mapStateToProps, {
  onSideNavToggle,
  signOut,
  onUserInfoChange,
  onLocaleChange,
})(MobileSlideBarNav);
